import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { Modal } from "antd";
import useBreakpoint from "../../../../utils/useBreakpoint";
import { LandTourPriceDate } from "../../../../interfaces/land-tour.interface";
import GroundTourModal from "./GroundTourModal";

const ICONS = "/iceholidays-assets/images/icons";
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export interface TravelDateOption {
  date: string;
  priceDate: LandTourPriceDate;
}

interface Props {
  open: boolean;
  onClose: () => void;
  // Every bookable departure date for the selected category, sorted ascending.
  options: TravelDateOption[];
  selectedDate: string | undefined;
  onConfirm: (option: TravelDateOption) => void;
}

export default function TravelDateModal({ open, onClose, options, selectedDate, onConfirm }: Props) {
  const isMobile = useBreakpoint();
  // Unmounting while closed means every opening starts on the chosen date's month with no stale pick.
  if (!open) return null;

  if (isMobile) {
    return (
      <GroundTourModal
        open={open}
        onClose={onClose}
        title="Travel Date"
        isMobile
        className="ground-tour-show-v2__calendar-popup"
      >
        <TravelDateSheet options={options} selectedDate={selectedDate} onConfirm={onConfirm} onClose={onClose} />
      </GroundTourModal>
    );
  }

  return (
    <TravelDateDialog options={options} selectedDate={selectedDate} onConfirm={onConfirm} onClose={onClose} />
  );
}

const monthKey = (date: string) => date.slice(0, 7);
const shiftMonth = (month: string, by: number) => dayjs(`${month}-01`).add(by, "month").format("YYYY-MM");

function useTravelDates(options: TravelDateOption[], selectedDate: string | undefined, monthsOnScreen: number) {
  const byDate = useMemo(() => new Map(options.map((option) => [option.date, option])), [options]);
  const firstMonth = options.length ? monthKey(options[0].date) : undefined;
  const lastMonth = options.length ? monthKey(options[options.length - 1].date) : undefined;
  // The furthest the window can start while still ending on the last month with dates.
  const lastStart = lastMonth && firstMonth ? [firstMonth, shiftMonth(lastMonth, 1 - monthsOnScreen)].sort().pop() : undefined;

  const [pending, setPending] = useState(selectedDate);
  const [month, setMonth] = useState(() => {
    const selectedMonth = selectedDate && monthKey(selectedDate);
    if (!selectedMonth || !firstMonth || !lastStart) return firstMonth;
    return [firstMonth, selectedMonth, lastStart].sort()[1];
  });

  const confirm = (onConfirm: (option: TravelDateOption) => void, onClose: () => void) => {
    const chosen = pending ? byDate.get(pending) : undefined;
    if (chosen && chosen.date !== selectedDate) onConfirm(chosen);
    onClose();
  };

  return { byDate, firstMonth, lastMonth, pending, setPending, month, setMonth, confirm };
}

function MonthNav({ direction, disabled, onClick }: { direction: "prev" | "next"; disabled: boolean; onClick: () => void }) {
  const mask = `url(${ICONS}/icon-calendar-${direction}.svg)`;

  return (
    <button
      type="button"
      className="ground-tour-show-v2__calendar-nav"
      disabled={disabled}
      onClick={onClick}
      aria-label={direction === "prev" ? "Previous month" : "Next month"}
    >
      <span aria-hidden="true" style={{ WebkitMaskImage: mask, maskImage: mask }} />
    </button>
  );
}

function MonthDays({
  month,
  byDate,
  pending,
  onPick,
}: {
  month: string;
  byDate: Map<string, TravelDateOption>;
  pending: string | undefined;
  onPick: (date: string) => void;
}) {
  const first = dayjs(`${month}-01`);
  const leadingBlanks = first.day();
  const dayCount = first.daysInMonth();

  return (
    <div className="ground-tour-show-v2__calendar-body">
      <div className="ground-tour-show-v2__calendar-weekdays" aria-hidden="true">
        {WEEKDAYS.map((weekday) => (
          <span key={weekday}>{weekday}</span>
        ))}
      </div>

      <div className="ground-tour-show-v2__calendar-days">
        {Array.from({ length: leadingBlanks }, (_, index) => (
          <span key={`blank-${index}`} aria-hidden="true" />
        ))}

        {Array.from({ length: dayCount }, (_, index) => {
          const date = `${month}-${String(index + 1).padStart(2, "0")}`;
          const isSelected = pending === date;

          return (
            <button
              key={date}
              type="button"
              className={`ground-tour-show-v2__calendar-day ${isSelected ? "is-selected" : ""}`}
              disabled={!byDate.has(date)}
              aria-pressed={isSelected}
              aria-label={dayjs(date).format("D MMMM YYYY")}
              onClick={() => onPick(date)}
            >
              <span>{index + 1}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const EmptyState = () => (
  <p className="ground-tour-show-v2__calendar-empty">No dates are available for this category.</p>
);

function TravelDateSheet({ options, selectedDate, onConfirm, onClose }: Omit<Props, "open">) {
  const { byDate, firstMonth, lastMonth, pending, setPending, month, setMonth, confirm } = useTravelDates(
    options,
    selectedDate,
    1
  );

  if (!month || !firstMonth || !lastMonth) return <EmptyState />;

  return (
    <div className="ground-tour-show-v2__calendar">
      <div className="ground-tour-show-v2__calendar-head">
        <MonthNav direction="prev" disabled={month <= firstMonth} onClick={() => setMonth(shiftMonth(month, -1))} />

        <span className="ground-tour-show-v2__calendar-month" aria-live="polite">
          {dayjs(`${month}-01`).format("MMMM YYYY")}
        </span>

        <MonthNav direction="next" disabled={month >= lastMonth} onClick={() => setMonth(shiftMonth(month, 1))} />
      </div>

      <MonthDays month={month} byDate={byDate} pending={pending} onPick={setPending} />

      <button type="button" className="ground-tour-show-v2__calendar-done" onClick={() => confirm(onConfirm, onClose)}>
        Done
      </button>
    </div>
  );
}

function TravelDateDialog({ options, selectedDate, onConfirm, onClose }: Omit<Props, "open">) {
  const { byDate, firstMonth, lastMonth, pending, setPending, month, setMonth, confirm } = useTravelDates(
    options,
    selectedDate,
    2
  );

  // Follows the pending pick, so it previews the choice before Apply.
  const pickedLabel = pending ? dayjs(pending).format("DD MMM YYYY") : null;
  const closeIcon = (
    <span className="ground-tour-show-v2__calendar-close">
      <img src={`${ICONS}/icon-calendar-close.svg`} alt="" aria-hidden="true" />
    </span>
  );

  return (
    <Modal
      open
      onCancel={onClose}
      centered
      footer={null}
      width={720}
      closeIcon={closeIcon}
      title={
        <>
          <span className="ground-tour-show-v2__calendar-when">When</span>
          {pickedLabel && <span className="ground-tour-show-v2__calendar-range">{pickedLabel}</span>}
        </>
      }
      rootClassName="ground-tour-show-v2__calendar-popup--desktop"
    >
      {!month || !firstMonth || !lastMonth ? (
        <EmptyState />
      ) : (
        <>
          <div className="ground-tour-show-v2__calendar-months">
            {[month, shiftMonth(month, 1)].map((pane, index) => (
              <React.Fragment key={pane}>
                {index === 1 && <div className="ground-tour-show-v2__calendar-divider" />}

                <div className="ground-tour-show-v2__calendar-pane">
                  <div className="ground-tour-show-v2__calendar-head">
                    {index === 0 ? (
                      <MonthNav
                        direction="prev"
                        disabled={month <= firstMonth}
                        onClick={() => setMonth(shiftMonth(month, -1))}
                      />
                    ) : (
                      <span className="ground-tour-show-v2__calendar-spacer" aria-hidden="true" />
                    )}

                    <span className="ground-tour-show-v2__calendar-month" aria-live="polite">
                      {dayjs(`${pane}-01`).format("MMMM YYYY")}
                    </span>

                    {index === 1 ? (
                      <MonthNav
                        direction="next"
                        disabled={pane >= lastMonth}
                        onClick={() => setMonth(shiftMonth(month, 1))}
                      />
                    ) : (
                      <span className="ground-tour-show-v2__calendar-spacer" aria-hidden="true" />
                    )}
                  </div>

                  <MonthDays month={pane} byDate={byDate} pending={pending} onPick={setPending} />
                </div>
              </React.Fragment>
            ))}
          </div>

          <div className="ground-tour-show-v2__calendar-footer">
            <button type="button" className="ground-tour-show-v2__calendar-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="ground-tour-show-v2__calendar-apply" onClick={() => confirm(onConfirm, onClose)}>
              Apply
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
